import React, { useState } from 'react';
import { useCurrency } from '../contexts/CurrencyContext';

/**
 * Currency Conversion Debug Panel
 * Shows real-time status of currency conversion system
 * Only visible in development or when ?debug=currency is in URL
 */
const CurrencyDebug = () => {
  const {
    currency,
    userCountry,
    exchangeRates,
    isLoading,
    convertPrice,
    formatPrice,
    lastFetchSuccess,
    currencySymbol
  } = useCurrency();

  const [isExpanded, setIsExpanded] = useState(false);

  // Only show in development or with ?debug=currency query param
  const isDevelopment = process.env.NODE_ENV === 'development';
  const hasDebugParam = window.location.search.includes('debug=currency');

  if (!isDevelopment && !hasDebugParam) {
    return null;
  }

  // Check if API key is configured
  const hasApiKey = !!process.env.REACT_APP_OPEN_EXCHANGE_RATES_API_KEY &&
                    process.env.REACT_APP_OPEN_EXCHANGE_RATES_API_KEY !== 'YOUR_API_KEY_HERE';

  // Test conversion
  const testAmount = 249;
  const convertedAmount = convertPrice(testAmount);
  const formattedPrice = formatPrice(testAmount);

  // Get cache status
  const cachedRates = localStorage.getItem('currency_exchange_rates');
  const cachedTimestamp = localStorage.getItem('currency_rates_timestamp');
  const hasCachedRates = !!cachedRates;

  // Calculate cache age
  let cacheAge = 'N/A';
  if (cachedTimestamp) {
    const cacheDate = new Date(cachedTimestamp);
    const ageMs = Date.now() - cacheDate.getTime();
    const ageHours = Math.floor(ageMs / (1000 * 60 * 60));
    const ageMinutes = Math.floor((ageMs % (1000 * 60 * 60)) / (1000 * 60));
    cacheAge = `${ageHours}h ${ageMinutes}m ago`;
  }

  // Determine overall status
  let status = 'unknown';
  let statusColor = '#999';
  let statusMessage = 'Checking...';

  if (!hasApiKey) {
    status = 'no-api-key';
    statusColor = '#dc3545';
    statusMessage = 'API Key Missing';
  } else if (isLoading) {
    status = 'loading';
    statusColor = '#ffc107';
    statusMessage = 'Loading Rates...';
  } else if (lastFetchSuccess) {
    status = 'success';
    statusColor = '#28a745';
    statusMessage = 'Working Perfectly';
  } else if (hasCachedRates) {
    status = 'cached';
    statusColor = '#17a2b8';
    statusMessage = 'Using Cached Rates';
  } else {
    status = 'error';
    statusColor = '#dc3545';
    statusMessage = 'API Error - No Cache';
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      zIndex: 9999,
      fontFamily: 'monospace',
      fontSize: '12px'
    }}>
      {/* Collapsed Badge */}
      {!isExpanded && (
        <div
          onClick={() => setIsExpanded(true)}
          style={{
            backgroundColor: statusColor,
            color: 'white',
            padding: '8px 12px',
            borderRadius: '20px',
            cursor: 'pointer',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: 'bold'
          }}
        >
          💱 {currency} {statusMessage === 'Working Perfectly' ? '✓' : '⚠'}
        </div>
      )}

      {/* Expanded Panel */}
      {isExpanded && (
        <div style={{
          backgroundColor: 'white',
          border: '2px solid ' + statusColor,
          borderRadius: '10px',
          padding: '15px',
          minWidth: '350px',
          maxWidth: '400px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px',
            paddingBottom: '10px',
            borderBottom: '2px solid #eee'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px' }}>💱</span>
              <strong style={{ fontSize: '14px' }}>Currency Debug Panel</strong>
            </div>
            <button
              onClick={() => setIsExpanded(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '0'
              }}
            >
              ✕
            </button>
          </div>

          {/* Status Section */}
          <div style={{
            backgroundColor: statusColor,
            color: 'white',
            padding: '10px',
            borderRadius: '5px',
            marginBottom: '15px',
            textAlign: 'center',
            fontWeight: 'bold'
          }}>
            {statusMessage}
          </div>

          {/* Details Grid */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>

            {/* API Key Status */}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #eee' }}>
              <span style={{ color: '#666' }}>API Key:</span>
              <span style={{ fontWeight: 'bold', color: hasApiKey ? '#28a745' : '#dc3545' }}>
                {hasApiKey ? '✓ Configured' : '✗ Missing'}
              </span>
            </div>

            {/* Detected Country */}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #eee' }}>
              <span style={{ color: '#666' }}>Detected Country:</span>
              <span style={{ fontWeight: 'bold' }}>{userCountry}</span>
            </div>

            {/* Current Currency */}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #eee' }}>
              <span style={{ color: '#666' }}>Active Currency:</span>
              <span style={{ fontWeight: 'bold' }}>{currency} ({currencySymbol})</span>
            </div>

            {/* Exchange Rates Count */}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #eee' }}>
              <span style={{ color: '#666' }}>Loaded Rates:</span>
              <span style={{ fontWeight: 'bold' }}>{Object.keys(exchangeRates).length} currencies</span>
            </div>

            {/* Last Fetch Status */}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #eee' }}>
              <span style={{ color: '#666' }}>Last API Fetch:</span>
              <span style={{ fontWeight: 'bold', color: lastFetchSuccess ? '#28a745' : '#dc3545' }}>
                {lastFetchSuccess ? '✓ Success' : '✗ Failed'}
              </span>
            </div>

            {/* Cache Status */}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #eee' }}>
              <span style={{ color: '#666' }}>Cache Status:</span>
              <span style={{ fontWeight: 'bold', color: hasCachedRates ? '#17a2b8' : '#999' }}>
                {hasCachedRates ? `✓ Active (${cacheAge})` : '✗ Empty'}
              </span>
            </div>

            {/* Test Conversion */}
            <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
              <div style={{ marginBottom: '5px', color: '#666' }}>Test Conversion:</div>
              <div style={{ fontWeight: 'bold' }}>
                NZ${testAmount} → {formattedPrice}
              </div>
              <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>
                (Raw: {convertedAmount})
              </div>
            </div>

            {/* Warnings/Errors */}
            {!hasApiKey && (
              <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '5px', color: '#856404' }}>
                <strong>⚠ Action Required:</strong><br/>
                Add API key to .env file:<br/>
                <code style={{ fontSize: '10px' }}>REACT_APP_OPEN_EXCHANGE_RATES_API_KEY=your_key</code>
              </div>
            )}

            {!lastFetchSuccess && !hasCachedRates && (
              <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f8d7da', borderRadius: '5px', color: '#721c24' }}>
                <strong>✗ Critical:</strong><br/>
                API fetch failed and no cached rates available. Currency conversion is not working.
              </div>
            )}

            {!lastFetchSuccess && hasCachedRates && (
              <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#d1ecf1', borderRadius: '5px', color: '#0c5460' }}>
                <strong>ℹ Info:</strong><br/>
                API unavailable, but using cached rates. Will retry every 5 minutes.
              </div>
            )}

            {/* Refresh Button */}
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: '10px',
                padding: '8px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              🔄 Refresh Page
            </button>

            {/* Close Instructions */}
            <div style={{ marginTop: '10px', fontSize: '10px', color: '#999', textAlign: 'center' }}>
              Remove ?debug=currency from URL to hide in production
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrencyDebug;
